"use client";

import { Core } from "@faire/design-tokens";
import { Typography } from "@faire/web--source/slate/Typography";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import * as React from "react";

import { ImageUploaderDropzone } from "./ImageUploaderDropzone";
import { useImageUploaderState } from "./useImageUploaderState";

export interface ImageDisplayProps {
  onFileUpload: (file: File) => Promise<void>;
  height: number;
  width: number;
  onUploadError?: (message: string) => void;
  uploadCta?: string;
  currentImage?: IImage;
  maxFileSize?: number;
  circular?: boolean;
  /**
   * This prop is used if the reupload functionality is controlled by an external button,
   * meaning the uploader should not be clickable if there is an image already uploaded.
   */
  externallyManagedReupload?: boolean;
  error?: boolean;
  displayRestrictions?: boolean;
}

/**
 * @trackcomponent
 * @link [Docs](https://web-docs.faire.team/slate/ImageUploader)
 */
export const ImageUploader: React.FC<ImageDisplayProps> = ({
  onFileUpload,
  onUploadError,
  currentImage,
  uploadCta,
  height,
  width,
  maxFileSize,
  circular,
  externallyManagedReupload = false,
  error = false,
  displayRestrictions,
}) => {
  const imageUploaderState = useImageUploaderState(onFileUpload);
  return (
    <>
      <ImageUploaderDropzone
        externallyManagedReupload={externallyManagedReupload}
        currentImage={currentImage}
        onImageUpload={imageUploaderState.handleImageUpload}
        onUploadError={
          onUploadError ? onUploadError : imageUploaderState.setErrorMessage
        }
        uploadCta={uploadCta}
        error={!!imageUploaderState.errorMessage || error}
        loading={!!imageUploaderState.loading}
        height={height}
        width={width}
        maxFileSize={maxFileSize}
        circular={circular}
        displayRestrictions={displayRestrictions}
      />
      {imageUploaderState.errorMessage ? (
        <Typography color={Core.text.critical}>
          {imageUploaderState.errorMessage}
        </Typography>
      ) : null}
    </>
  );
};
